import {
  addCalendarDays,
  computeNextOccurrence,
  isInRange,
  isOnOrBefore,
} from './compute-next-occurrence';
import type { ApprovalMode, RecurrenceRule } from './types';

/**
 * How far ahead a MANUAL rule materializes its occurrences.
 *
 * Manual approval is only useful if you see the due date coming *before* it
 * lands: with a lead of 0 you would always be approving late. One week is
 * enough to prepare a transfer without cluttering the inbox with next month.
 */
export const MANUAL_LEAD_DAYS = 7;

/**
 * Hard ceiling on what a single roll-forward pass materializes for one rule.
 *
 * A rule created with a start date years in the past would otherwise emit
 * hundreds of rows in one go. We stop at the cap and report `truncated` —
 * the cursor stays where we stopped, so the next pass simply continues.
 * Nothing is ever silently dropped.
 */
export const MAX_OCCURRENCES_PER_RUN = 60;

export interface SchedulePlanRule extends RecurrenceRule {
  id: string;
  isActive: boolean;
  approvalMode: ApprovalMode;
  /** Cursor stored on the rule: the first date not yet materialized. */
  nextOccurrenceDate: string;
}

export interface PlannedOccurrence {
  ruleId: string;
  /** Date produced by the cadence — immutable, and the uniqueness key. */
  scheduledDate: string;
  /** True when the pass should turn it into a payment immediately. */
  autoPost: boolean;
}

export interface RulePlan {
  ruleId: string;
  /** Occurrences to insert (dates already present are filtered out). */
  occurrences: PlannedOccurrence[];
  /** Cursor to write back on the rule. */
  newNextOccurrenceDate: string;
  /** Latest scheduled date reached, or null when nothing was produced. */
  lastGeneratedDate: string | null;
  /** True when the cursor ran past `endDate` — the rule has nothing left to emit. */
  exhausted: boolean;
  /** True when the per-run cap stopped us early; the next pass resumes here. */
  truncated: boolean;
}

export interface PlanOptions {
  /** Days of look-ahead for manual rules. Defaults to {@link MANUAL_LEAD_DAYS}. */
  leadDays?: number;
  /** Per-rule ceiling for one pass. Defaults to {@link MAX_OCCURRENCES_PER_RUN}. */
  maxPerRun?: number;
  /**
   * Scheduled dates already materialized for this rule. Those are skipped
   * (the cursor still advances past them), which makes the plan safe to
   * recompute after a cursor drift — a restore, a manual DB edit, or two
   * devices racing the same pass.
   */
  existingScheduledDates?: ReadonlySet<string>;
}

/**
 * Decide what a rule owes as of `today`.
 *
 * Automatic rules only reach up to today: an automatic payment must not be
 * booked before its due date. Manual rules reach `leadDays` further so the
 * approval inbox can show what is about to happen.
 */
export function planOccurrencesForRule(
  rule: SchedulePlanRule,
  today: string,
  options: PlanOptions = {},
): RulePlan {
  const leadDays = options.leadDays ?? MANUAL_LEAD_DAYS;
  const maxPerRun = options.maxPerRun ?? MAX_OCCURRENCES_PER_RUN;
  const existing = options.existingScheduledDates;

  const horizon =
    rule.approvalMode === 'manual' ? addCalendarDays(today, Math.max(0, leadDays)) : today;

  const occurrences: PlannedOccurrence[] = [];
  let cursor = rule.nextOccurrenceDate;
  let lastGeneratedDate: string | null = null;
  let truncated = false;
  let emitted = 0;

  if (rule.isActive) {
    while (isOnOrBefore(cursor, horizon)) {
      if (!isInRange(cursor, rule.startDate, rule.endDate)) break;

      if (emitted >= maxPerRun) {
        truncated = true;
        break;
      }

      if (!existing?.has(cursor)) {
        occurrences.push({
          ruleId: rule.id,
          scheduledDate: cursor,
          autoPost: rule.approvalMode === 'automatic' && isOnOrBefore(cursor, today),
        });
      }
      lastGeneratedDate = cursor;
      emitted += 1;
      cursor = computeNextOccurrence(rule, cursor);
    }
  }

  return {
    ruleId: rule.id,
    occurrences,
    newNextOccurrenceDate: cursor,
    lastGeneratedDate,
    // Exhausted = the cursor walked past the end date, so the rule can never
    // emit again. A rule merely stopped by the per-run cap still has work left.
    exhausted: rule.endDate !== null && cursor > rule.endDate,
    truncated,
  };
}

export interface SchedulePlan {
  plans: RulePlan[];
  /** Flat list across every rule, ordered by date then rule. */
  occurrences: PlannedOccurrence[];
  /** Rules whose per-run cap was hit — surfaced so the UI never lies about coverage. */
  truncatedRuleIds: string[];
}

/** Bulk variant of {@link planOccurrencesForRule}. */
export function planSchedule(
  rules: SchedulePlanRule[],
  today: string,
  options: PlanOptions & {
    /** Per-rule sets of already-materialized dates, keyed by rule id. */
    existingByRule?: ReadonlyMap<string, ReadonlySet<string>>;
  } = {},
): SchedulePlan {
  const plans = rules.map((rule) =>
    planOccurrencesForRule(rule, today, {
      ...options,
      existingScheduledDates:
        options.existingByRule?.get(rule.id) ?? options.existingScheduledDates,
    }),
  );

  const occurrences = plans
    .flatMap((p) => p.occurrences)
    .sort((a, b) =>
      a.scheduledDate === b.scheduledDate
        ? a.ruleId.localeCompare(b.ruleId)
        : a.scheduledDate.localeCompare(b.scheduledDate),
    );

  return {
    plans,
    occurrences,
    truncatedRuleIds: plans.filter((p) => p.truncated).map((p) => p.ruleId),
  };
}

/**
 * Dates the cadence will produce after `from`, without materializing anything.
 *
 * Used by the rule editor ("next: 01/08, 01/09, 01/10") and the forecast tab.
 * Stops early at `endDate`, so a finite rule returns fewer than `count`.
 */
export function projectUpcoming(rule: RecurrenceRule, from: string, count: number): string[] {
  const dates: string[] = [];
  let cursor = from;

  // The anchor itself counts when it is a legal occurrence of the rule.
  if (isInRange(cursor, rule.startDate, rule.endDate)) dates.push(cursor);

  while (dates.length < count) {
    cursor = computeNextOccurrence(rule, cursor);
    if (!isInRange(cursor, rule.startDate, rule.endDate)) break;
    dates.push(cursor);
  }

  return dates.slice(0, count);
}
